/**
 * F65 — admin security keys client.
 *
 *   GET    /admin/me/security-keys                → list         { data }
 *   POST   /admin/me/security-keys/options        → beginAdd     { data }
 *   POST   /admin/me/security-keys                → finishAdd    { data }
 *   DELETE /admin/me/security-keys/:id            → remove       204
 *   POST   /admin/auth/login/security-key/options → beginLogin   bare body, as
 *                                                   every /admin/auth/* route
 *
 * The final login call lives in `AuthProvider.confirmSecurityKey` beside the TOTP
 * one, because it opens the session. The vitest config aliases this module to
 * `securityKeys.fixture`; the real request shapes are pinned by
 * test/client-contracts-security-keys.test.ts.
 */
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import type { AdminSecurityKey } from '@bravemove/types';
import { adminFetch } from '../auth/apiClient';

export interface SecurityKeyCeremony<TOptions> {
  readonly challengeId: string;
  readonly options: TOptions;
}

export interface FinishAddInput {
  readonly challengeId: string;
  readonly name: string;
  readonly attestation: RegistrationResponseJSON;
}

const BASE = '/admin/me/security-keys';

export const adminSecurityKeysApi = {
  list: async (): Promise<readonly AdminSecurityKey[]> => {
    const body = await adminFetch<{ readonly data: readonly AdminSecurityKey[] }>(BASE);
    return body.data;
  },

  beginAdd: async (
    password: string,
  ): Promise<SecurityKeyCeremony<PublicKeyCredentialCreationOptionsJSON>> => {
    const body = await adminFetch<{
      readonly data: SecurityKeyCeremony<PublicKeyCredentialCreationOptionsJSON>;
    }>(`${BASE}/options`, { method: 'POST', body: { password } });
    return body.data;
  },

  finishAdd: async (input: FinishAddInput): Promise<AdminSecurityKey> => {
    const body = await adminFetch<{ readonly data: AdminSecurityKey }>(BASE, {
      method: 'POST',
      body: { ...input },
    });
    return body.data;
  },

  remove: async (id: string, password: string): Promise<void> => {
    await adminFetch(`${BASE}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: { password },
    });
  },

  beginLogin: async (
    totpChallengeToken: string,
  ): Promise<SecurityKeyCeremony<PublicKeyCredentialRequestOptionsJSON>> =>
    adminFetch<SecurityKeyCeremony<PublicKeyCredentialRequestOptionsJSON>>(
      '/admin/auth/login/security-key/options',
      { method: 'POST', body: { totpChallengeToken } },
    ),
};
